import { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import environment from '../utils/environment';
import { AppConfigContext } from '../contexts/appConfigContext';
import { logInfo, logWarning } from '../utils/remoteLogger';
import sessionStorage from '../utils/sessionStorage';

export interface IGtmEvent {
  eventToken: string;
}

const TRACKING_KEYS = [
  'gclid',
  'gbraid',
  'wbraid',
  'fbclid',
  'ScCid',
  'ttclid',
  'msclkid',
];

const TRACKING_IDS_SESSION_KEY = 'gtm_tracking_ids';

const localEvents = {
  kry: {
    REGISTRATION: { eventToken: 'h09w47' },
  },

  livi: {
    REGISTRATION: { eventToken: 'ca7r7f' },
  },
};

interface IGtmEventPayload {
  eventName: string;
  eventToken: string;
  eventId: string;
  app_country: string;
  userId?: string;
  trackingIds?: Record<string, string>;
  eventSourceUrl: string;
  timestamp: string;
}

const generateEventId = (): string => {
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID();
  }
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.getRandomValues === 'function'
  ) {
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    // RFC 4122 v4: top 4 bits of byte 6 = 0100, top 2 bits of byte 8 = 10.
    // Arithmetic form used because the project's lint config bans bitwise ops.
    bytes[6] = (bytes[6] % 16) + 0x40;
    bytes[8] = (bytes[8] % 64) + 0x80;
    const hex: string[] = [];
    for (let i = 0; i < 16; i += 1) {
      hex.push(bytes[i].toString(16).padStart(2, '0'));
    }
    return `${hex.slice(0, 4).join('')}-${hex.slice(4, 6).join('')}-${hex
      .slice(6, 8)
      .join('')}-${hex.slice(8, 10).join('')}-${hex.slice(10, 16).join('')}`;
  }
  // Crypto API unavailable — timestamp + random is not cryptographically
  // strong but sufficient for event deduplication in this context.
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
};

// Module-level shared state across all hook instances
let isEnabled = false;
let eventQueue: IGtmEventPayload[] = [];
const trackingIds: Record<string, string> = {};

const useGtmEvents = () => {
  const { userId } = useContext(AppConfigContext);

  const userIdRef = useRef(userId);

  const event = environment.IS_KRY_APP ? localEvents.kry : localEvents.livi;

  const getGtmServerUrl = useCallback((): string | null => {
    const host = window.location.host.toLowerCase();
    const isTest = host.includes('test') || host.includes('localhost');

    if (isTest) {
      return 'https://events.app.test-no-growth.kry.se';
    }

    if (environment.IS_PROD) {
      switch (environment.COUNTRY) {
        case 'SE':
          return 'https://events.app.kry.se';
        case 'FR':
          return environment.DOMAIN === 'kry.fr'
            ? 'https://events.app.kry.fr'
            : 'https://events.app.livi.fr';
        case 'GB':
        case 'NO':
          // Don't send events for UK/NO production
          return null;
        default:
          return null;
      }
    }

    return null;
  }, []);

  const getTrackingIds = useCallback(() => {
    if (typeof window === 'undefined') return {};
    const trackingParams: Record<string, string> = {};
    const params = new URLSearchParams(window.location.search);
    TRACKING_KEYS.forEach((key) => {
      const value = params?.get(key);
      if (value) {
        trackingParams[key] = value;
      }
    });

    return trackingParams;
  }, []);

  const initialize = useCallback(() => {
    const raw = sessionStorage.get<Record<string, string>>(
      TRACKING_IDS_SESSION_KEY
    );
    const storedIds: Record<string, string> =
      raw && typeof raw === 'object' ? raw : {};
    const urlIds = getTrackingIds();
    const merged = { ...storedIds, ...urlIds };

    Object.assign(trackingIds, merged);

    if (Object.keys(merged).length > 0) {
      sessionStorage.set(TRACKING_IDS_SESSION_KEY, merged);
    }

    const serverUrl = getGtmServerUrl();
    logInfo('useGtmEvents', 'Initialized GTM Events (disabled by default)', {
      serverUrl: serverUrl || 'not configured',
      trackingIds: JSON.stringify(trackingIds),
    });
  }, [getTrackingIds, getGtmServerUrl]);

  const sendEventToServer = useCallback(
    async (eventPayload: IGtmEventPayload) => {
      const serverUrl = getGtmServerUrl();

      if (!serverUrl) {
        logInfo(
          'useGtmEvents',
          'GTM server URL not configured for this environment/country',
          {
            country: environment.COUNTRY,
            isProd: String(environment.IS_PROD),
          }
        );
        return;
      }

      try {
        const response = await fetch(serverUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(eventPayload),
        });

        if (!response.ok) {
          throw new Error(
            `GTM server responded with status: ${response.status}`
          );
        }

        logInfo('useGtmEvents', 'Event sent successfully', {
          eventToken: eventPayload.eventToken,
          eventName: eventPayload.eventName,
          serverUrl,
          trackingIds: JSON.stringify(eventPayload.trackingIds),
        });
      } catch (error) {
        logWarning('useGtmEvents', 'Failed to send event to GTM server', {
          error: error instanceof Error ? error.message : String(error),
          eventPayload: JSON.stringify(eventPayload),
        });
      }
    },
    [getGtmServerUrl]
  );

  const processEventQueue = useCallback(async () => {
    if (eventQueue.length === 0) {
      return;
    }

    logInfo('useGtmEvents', 'Processing queued events', {
      queueSize: String(eventQueue.length),
    });

    const eventsToSend = [...eventQueue];
    eventQueue = [];

    // Send all events in parallel
    await Promise.all(
      eventsToSend.map((eventData) => sendEventToServer(eventData))
    );
  }, [sendEventToServer]);

  const enableTracking = useCallback(() => {
    if (!isEnabled) {
      isEnabled = true;
      logInfo('useGtmEvents', 'Events enabled - processing queued events');
      // Process any queued events when tracking is enabled
      void processEventQueue();
    }
  }, [processEventQueue]);

  const disableTracking = useCallback(() => {
    if (isEnabled) {
      isEnabled = false;
      logInfo('useGtmEvents', 'Events disabled - events will be queued');
    }
  }, []);

  const reset = useCallback(() => {
    isEnabled = false;
    eventQueue = [];
    logInfo('useGtmEvents', 'Reset: cleared queue and event data');
  }, []);

  const trackEvent = useCallback(
    (gtmEvent: IGtmEvent, explicitUserId?: string) => {
      const eventPayload: IGtmEventPayload = {
        eventName: 'custom_event',
        eventToken: gtmEvent.eventToken,
        eventId: generateEventId(),
        app_country: environment.COUNTRY,
        eventSourceUrl: window.location.origin,
        timestamp: new Date().toISOString(),
      };

      const currentUserId = explicitUserId || userIdRef.current;
      if (currentUserId) {
        eventPayload.userId = currentUserId;
      }

      // Add tracking IDs captured at initialization (persisted for tab lifetime)
      if (Object.keys(trackingIds).length > 0) {
        eventPayload.trackingIds = trackingIds;
      }

      // If tracking is disabled, queue the event
      if (!isEnabled) {
        eventQueue.push(eventPayload);
        logInfo('useGtmEvents', 'Events disabled - event queued', {
          eventToken: gtmEvent.eventToken,
          queueSize: String(eventQueue.length),
        });
        return;
      }

      // Send the event immediately when tracking is enabled
      sendEventToServer(eventPayload);
    },
    [sendEventToServer]
  );

  // Update mutable refs.
  useEffect(() => {
    userIdRef.current = userId;
  }, [userId]);

  // Use useMemo to create a stable tracker object that only changes when its dependencies change
  return useMemo(
    () => ({
      event,
      initialize,
      enableTracking,
      disableTracking,
      trackEvent,
      reset,
    }),
    [event, initialize, enableTracking, disableTracking, trackEvent, reset]
  );
};

export default useGtmEvents;
